// src/services/workItemChatService.js
//
// Universal Work Item Chat API client
// ===================================
//
// PURPOSE
// -------
// This file is the thin frontend transport layer for universal chat endpoints
// attached to any work item key, for example:
//
//   - "todo:123"
//   - "leave:9223372036854775807"
//
// CURRENT APP ARCHITECTURE
// ------------------------
// Your current architecture is now very clear:
//
// 1) HTTP is authoritative
//    - chat messages are created via HTTP
//    - chat history is loaded via HTTP
//    - read-state is persisted via HTTP
//
// 2) Socket.IO is only a realtime hint
//    - backend emits socket events AFTER DB commit
//    - frontend may append a message optimistically from socket,
//      but HTTP remains the source of truth
//
// 3) Auth/session handling does NOT belong here
//    - apiClient owns refresh-on-401
//    - AuthContext/pages decide whether to softLogout on 401
//    - this service must stay transport-focused
//
// DESIGN RULES FOR THIS FILE
// --------------------------
// - keep functions small and predictable
// - support AbortSignal
// - do not perform UI actions
// - do not trigger logout
// - do not patch frontend state directly
// - fail early on obviously invalid input
//
// BACKEND CONTRACTS USED HERE
// ---------------------------
// GET  /work-items/:key/chat
//   -> { thread, messages }
//
// POST /work-items/:key/chat/messages
//   body: { content }
//   -> created message DTO (with user include)
//
// POST /work-items/:key/chat/read
//   -> { message: "...", requestId? } or similar small ack payload
//
// IMPORTANT
// ---------
// This service intentionally performs only LIGHT response validation.
// The backend remains the final authority for structure and permissions.

import apiClient from "../utils/apiClient";
import { isRequestCanceled, assertWorkItemKey } from "./httpUtils";

const DEV = process.env.NODE_ENV !== "production";

/* -------------------------------------------------------------------------- */
/* Constants                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Keep this aligned with backend/controllers/workItemChatController.js
 */
const MAX_CHAT_MESSAGE_LEN = 2000;

/* -------------------------------------------------------------------------- */
/* Small helpers                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Build the common base path for all chat endpoints of one work item.
 *
 * We always URL-encode the key because:
 * - keys contain ":" by design
 * - future work item keys may contain additional safe-but-encoded characters
 */
function buildChatBasePath(workItemKey) {
  const key = assertWorkItemKey(workItemKey);
  return `/work-items/${encodeURIComponent(key)}/chat`;
}

/**
 * Normalize outgoing chat content.
 *
 * Why validate here if backend also validates?
 * - faster feedback for the user
 * - avoids pointless round-trips
 * - keeps UI behavior predictable
 *
 * IMPORTANT:
 * - backend must still validate independently
 */
function normalizeContent(raw) {
  const content = String(raw || "").trim();

  if (!content) return null;
  if (content.length > MAX_CHAT_MESSAGE_LEN) return null;

  return content;
}

/**
 * Decide whether this service should log the error.
 *
 * Philosophy:
 * - cancellations are normal control flow -> never log here
 * - expected 4xx are often handled better by the page/component
 * - unexpected failures are worth logging, especially in development
 */
function shouldLogServiceError(error) {
  if (isRequestCanceled(error)) return false;

  const status = error?.response?.status;

  // Expected client/business/auth errors are often already handled by callers.
  if (typeof status === "number" && status >= 400 && status < 500) {
    return DEV;
  }

  return true;
}

/**
 * Light validation of the GET /chat response.
 *
 * We do NOT deeply validate every message field, because:
 * - backend is authoritative
 * - DTOs may evolve
 * - page/component should tolerate optional fields
 *
 * We only ensure the minimum structure expected by callers.
 */
function assertValidChatPayload(data) {
  const isObject = data && typeof data === "object" && !Array.isArray(data);
  if (!isObject) {
    throw new Error("fetchWorkItemChat: backend returned an invalid payload.");
  }

  const hasThreadField = Object.prototype.hasOwnProperty.call(data, "thread");
  const hasMessagesArray = Array.isArray(data.messages);

  if (!hasThreadField || !hasMessagesArray) {
    throw new Error("fetchWorkItemChat: backend response is missing required fields.");
  }

  return data;
}

/**
 * Light validation for the created message response returned by POST /messages.
 *
 * We only check that it looks like one message object.
 */
function assertValidCreatedMessage(data) {
  const isObject = data && typeof data === "object" && !Array.isArray(data);
  if (!isObject) {
    throw new Error("sendWorkItemChatMessage: backend returned an invalid message payload.");
  }

  const hasId = Object.prototype.hasOwnProperty.call(data, "id");
  const hasContent = Object.prototype.hasOwnProperty.call(data, "content");

  if (!hasId || !hasContent) {
    throw new Error("sendWorkItemChatMessage: backend response is missing required message fields.");
  }

  return data;
}

/**
 * Light validation for mark-read acknowledgement payload.
 *
 * We keep this intentionally permissive because backend may return
 * a small generic DTO like:
 *   { message: "Marked as read." }
 * or
 *   { ok: true }
 */
function assertValidReadAck(data) {
  const isObject = data && typeof data === "object" && !Array.isArray(data);
  if (!isObject) {
    throw new Error("markWorkItemChatRead: backend returned an invalid acknowledgement payload.");
  }

  return data;
}

/* -------------------------------------------------------------------------- */
/* Public API                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Load chat thread + message history for one work item.
 *
 * Endpoint:
 *   GET /work-items/:key/chat
 *
 * Backend behavior:
 * - validates access to the work item
 * - ensures the thread exists
 * - returns message history
 * - may also mark the chat as read server-side
 *
 * Caller responsibilities:
 * - decide what to do on 401 (usually softLogout in the page layer)
 * - render messages
 * - optionally trigger additional UI behavior
 *
 * @param {string} workItemKey
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<{ thread: object|null, messages: Array }>}
 */
export async function fetchWorkItemChat(workItemKey, options = {}) {
  const basePath = buildChatBasePath(workItemKey);

  try {
    const response = await apiClient.get(basePath, {
      withCredentials: true,
      signal: options.signal,
    });

    return assertValidChatPayload(response?.data);
  } catch (error) {
    if (shouldLogServiceError(error)) {
      // eslint-disable-next-line no-console
      console.error("[workItemChatService] fetchWorkItemChat failed:", error);
    }

    throw error;
  }
}

/**
 * Send one chat message for a work item.
 *
 * Endpoint:
 *   POST /work-items/:key/chat/messages
 *
 * Backend behavior:
 * - validates access + content
 * - writes the message in DB
 * - commits
 * - emits realtime hints after commit
 *
 * Caller responsibilities:
 * - decide how to update UI
 * - decide what to do on 401
 *
 * @param {string} workItemKey
 * @param {string} content
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<object>} created message DTO
 */
export async function sendWorkItemChatMessage(workItemKey, content, options = {}) {
  const basePath = buildChatBasePath(workItemKey);

  const cleanContent = normalizeContent(content);
  if (!cleanContent) {
    const error = new Error(
      `Message content is required and must not exceed ${MAX_CHAT_MESSAGE_LEN} characters.`
    );
    error.code = "INVALID_CHAT_CONTENT";
    throw error;
  }

  try {
    const response = await apiClient.post(
      `${basePath}/messages`,
      { content: cleanContent },
      {
        withCredentials: true,
        signal: options.signal,
      }
    );

    return assertValidCreatedMessage(response?.data);
  } catch (error) {
    if (shouldLogServiceError(error)) {
      // eslint-disable-next-line no-console
      console.error("[workItemChatService] sendWorkItemChatMessage failed:", error);
    }

    throw error;
  }
}

/**
 * Explicitly mark chat as read for one work item.
 *
 * Endpoint:
 *   POST /work-items/:key/chat/read
 *
 * Backend behavior:
 * - updates WorkItemRead.last_read_at for the current user
 * - affects unread-chat indicators on Dashboard (/my-page)
 *
 * Typical usage:
 * - after opening a chat
 * - after appending a new message in the active thread
 * - as a best-effort side effect
 *
 * IMPORTANT:
 * This function should remain cheap and idempotent from the caller perspective.
 *
 * @param {string} workItemKey
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<object>} small acknowledgement payload
 */
export async function markWorkItemChatRead(workItemKey, options = {}) {
  const basePath = buildChatBasePath(workItemKey);

  try {
    const response = await apiClient.post(
      `${basePath}/read`,
      {},
      {
        withCredentials: true,
        signal: options.signal,
      }
    );

    return assertValidReadAck(response?.data);
  } catch (error) {
    if (shouldLogServiceError(error)) {
      // eslint-disable-next-line no-console
      console.error("[workItemChatService] markWorkItemChatRead failed:", error);
    }

    throw error;
  }
}